import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Badge, Row, Col } from 'react-bootstrap';
import { Clock, Building, User, FileText, Camera, MessageSquare, CheckSquare, Calendar, Tag, CheckCircle, Link as LinkIcon, ChevronDown, ChevronRight, ExternalLink, Clock as ClockIcon, AlignLeft, RefreshCw, Search, Wrench, Ruler } from 'lucide-react';
import { SnoozeModal } from '../../../Components/SnoozeModal';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { useUsersData } from '../../../Helpers/UsersContext';
import { UnifiedTask, getAssignmentNames } from './TaskCard';
import { workOrderPriorityColors, getComponentKeys } from '../../../types/workOrders';
import { BuildingSummary, BaseComponent } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { translateWorkOrderPriority, translateInspectionCategory } from '../../../types/EnumTranslations';
import { getEffectiveValues, formatInterval } from '../../BuildingChecks/buildingChecksUtils';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { formatDateForDisplay, formatDateTimeForDisplay } from '../../../Helpers/DateUtils';
import LocationSection from '../../../Components/LocationSection';
import CheckLocationSection from '../../BuildingChecks/components/CheckLocationSection';
import MiniMap from '../../../Components/MiniMap';
import { FirebaseImage, ImageSize } from '../../../Helpers/ImageLoader';
import { getCoverPhotoStoragePath } from '../../../Helpers/utils';
import { placeholderForBuildingType } from '../../../Helpers/PlaceholderUtils';
import up_right_arrow from '../../../Images/button-icons/up_right_arrow.png';
import './TaskDetailModal.css';

interface TaskDetailModalProps {
    show: boolean;
    onHide: () => void;
    task: UnifiedTask;
    onAssign: (task: UnifiedTask) => void;
    onNavigate: (task: UnifiedTask) => void;
    getUserName: (uid: string) => string;
    onSnooze?: (snoozeDate: string) => Promise<void>;
    onUnsnooze?: () => Promise<void>;
}

/**
 * Modal for viewing task details from the To Do column
 * Shows comprehensive information for both work orders and inspections
 */
const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
    show,
    onHide,
    task,
    onAssign,
    onNavigate,
    getUserName,
    onSnooze,
    onUnsnooze
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [componentName, setComponentName] = useState<string | null>(null);
    const [buildingSummary, setBuildingSummary] = useState<BuildingSummary | null>(null);
    const [component, setComponent] = useState<BaseComponentImpl | null>(null);
    const [showSnoozeModal, setShowSnoozeModal] = useState(false);
    const [unsnoozing, setUnsnoozing] = useState(false);
    const { users } = useUsersData();
    const auth = getAuth();
    const workOrder = task.workOrder;

    const isAdmin = users?.find(u => u.key === auth.currentUser?.uid)?.admin || false;
    const inspection = task.inspection;
    const { getTranslation } = useBuildComponentTypes();

    // Fetch building summary
    useEffect(() => {
        if (!show || !task.buildingKey) {
            setBuildingSummary(null);
            return;
        }

        const fetchBuildingSummary = async () => {
            try {
                const db = getDatabase();
                const summaryRef = ref(db, `buildingsSummary/${task.buildingKey}`);
                const snapshot = await get(summaryRef);
                if (snapshot.exists()) {
                    setBuildingSummary(snapshot.val() as BuildingSummary);
                }
            } catch (err) {
                console.error('Error fetching building summary:', err);
            }
        };

        fetchBuildingSummary();
    }, [show, task.buildingKey]);

    // A work order's first component (a check shows its own in CheckLocationSection)
    const taskComponentKey = workOrder ? getComponentKeys(workOrder)[0] : undefined;
    const taskComponentBuildingKey = workOrder?.buildingKey;

    // Fetch component name and data when modal opens
    useEffect(() => {
        if (!show || !taskComponentKey || !taskComponentBuildingKey) {
            setComponentName(null);
            setComponent(null);
            return;
        }

        const fetchComponentData = async () => {
            try {
                const db = getDatabase();
                const componentRef = ref(db, `buildings/${taskComponentBuildingKey}/structure/components/${taskComponentKey}`);
                const snapshot = await get(componentRef);
                if (snapshot.exists()) {
                    const componentData = snapshot.val() as BaseComponent;
                    const componentImpl = new BaseComponentImpl(componentData);
                    setComponent(componentImpl);

                    const label = componentData?.label?.value;
                    const specificType = componentData?.specificType?.value;
                    const typeName = specificType ? getTranslation(specificType, componentData.type) : null;

                    if (label && typeName) {
                        setComponentName(`${typeName} (${label})`);
                    } else if (label) {
                        setComponentName(label);
                    } else if (typeName) {
                        setComponentName(typeName);
                    } else {
                        setComponentName('Komponent');
                    }
                }
            } catch (err) {
                console.error('Error fetching component:', err);
                setComponentName('Komponent');
                setComponent(null);
            }
        };

        fetchComponentData();
    }, [show, taskComponentKey, taskComponentBuildingKey, getTranslation]);

    const effectiveInspectionValues = useMemo(() => {
        if (!inspection?.template) return null;
        return getEffectiveValues(inspection, inspection.template);
    }, [inspection]);

    const handleAssign = () => {
        onHide();
        onAssign(task);
    };

    const toggleDetails = () => setShowDetails(!showDetails);

    const handleSnooze = async (snoozeDate: string) => {
        if (onSnooze) await onSnooze(snoozeDate);
    };

    const handleUnsnooze = async () => {
        if (onUnsnooze) {
            setUnsnoozing(true);
            try {
                await onUnsnooze();
                onHide();
            } finally {
                setUnsnoozing(false);
            }
        }
    };

    const componentLink = taskComponentKey && taskComponentBuildingKey
        ? `/building/${taskComponentBuildingKey}/component/${taskComponentKey}`
        : null;

    const buildingLink = `/building/${task.buildingKey}`;

    // Building location data (for kontroll map)
    const effectiveCoordinates = buildingSummary?.coordinates;
    const hasCoordinates = !!(effectiveCoordinates?.latitude && effectiveCoordinates?.longitude);
    const buildingInfo = buildingSummary?.buildingInfo;
    const googleMapsUrl = hasCoordinates
        ? `https://maps.google.com/?q=${effectiveCoordinates!.latitude},${effectiveCoordinates!.longitude}`
        : buildingInfo?.street
            ? `https://maps.google.com/search/${encodeURIComponent([buildingInfo.street, buildingInfo.postalCode, buildingInfo.city].filter(Boolean).join(' '))}`
            : null;

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <Badge
                        bg={task.type === 'workOrder' ? 'workOrder' : 'inspection'}
                        className={`d-inline-flex align-items-center gap-1 ${task.type === 'workOrder' ? 'badge-work-order' : 'badge-inspection'}`}
                    >
                        {task.type === 'workOrder' ? <Wrench size={10} /> : <Search size={10} />}
                        {task.type === 'workOrder' ? 'Ärende' : 'Kontroll'}
                    </Badge>
                    {inspection?.template?.category && (
                        <Badge bg="secondary" className="badge-label">
                            {translateInspectionCategory(inspection.template.category)}
                        </Badge>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {task.type === 'inspection' ? (
                    /* ── Kontroll layout ── */
                    <>
                        <Row className="mb-4">
                            {/* Left: title, description, requirements */}
                            <Col md={6} className="mb-3 mb-md-0">
                                <h5 style={{ fontSize: 20 }}>{task.title}</h5>

                                {inspection && (
                                    <div className="mt-3">
                                        <CheckLocationSection buildingKey={task.buildingKey} check={inspection} />
                                    </div>
                                )}

                                {effectiveInspectionValues?.description && (
                                    <>
                                        <h6 className="task-detail-modal__section-title mt-3">
                                            <AlignLeft size={16} className="text-muted" />
                                            Beskrivning
                                        </h6>
                                        <p style={{ whiteSpace: 'pre-wrap' }} className="mb-0">
                                            {effectiveInspectionValues.description}
                                        </p>
                                    </>
                                )}

                                {inspection?.template && (inspection.template.photoRequired || inspection.template.takesMeasurement) && (
                                    <div className="mt-3">
                                        <h6 className="task-detail-modal__section-title">
                                            <CheckSquare size={16} className="text-muted" />
                                            Krav
                                        </h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            {inspection.template.photoRequired && (
                                                <Badge bg="light" text="dark" className="d-flex align-items-center gap-1">
                                                    <Camera size={12} /> Foto krävs
                                                </Badge>
                                            )}
                                            {inspection.template.takesMeasurement && (
                                                <Badge bg="light" text="dark" className="d-flex align-items-center gap-1">
                                                    <Ruler size={12} /> Mätning ({inspection.template.takesMeasurement.metric})
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Col>

                            {/* Right: building card + map */}
                            <Col md={6}>
                                <div className="task-detail-modal__building-card">
                                <div className="d-flex align-items-center gap-3 mb-2">
                                    <FirebaseImage
                                        storagePath={buildingSummary ? getCoverPhotoStoragePath(buildingSummary) : undefined}
                                        size={ImageSize.THUMBNAIL_SMALL}
                                        placeholder={placeholderForBuildingType(buildingSummary?.buildingType)}
                                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                                        alt={task.buildingName}
                                    />
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <strong>{task.buildingName}</strong>
                                        {buildingInfo?.street && (
                                            <div className="small text-muted mt-1">{buildingInfo.street}</div>
                                        )}
                                        {(buildingInfo?.postalCode || buildingInfo?.city) && (
                                            <div className="small text-muted">
                                                {[buildingInfo?.postalCode, buildingInfo?.city].filter(Boolean).join(' ')}
                                            </div>
                                        )}
                                    </div>
                                    {task.buildingKey && buildingSummary && (
                                        <a
                                            href={buildingLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="task-detail-modal__building-link-btn"
                                        >
                                            <ExternalLink size={16} />
                                        </a>
                                    )}
                                </div>

                                {hasCoordinates && (
                                    <div className="mt-3">
                                        <MiniMap
                                            coordinates={effectiveCoordinates!}
                                            buildingName={task.buildingName}
                                            height={160}
                                        />
                                    </div>
                                )}

                                {googleMapsUrl && (
                                    <Button
                                        variant="secondary"
                                        className="mt-2"
                                        style={{
                                            fontSize: '14px',
                                            backgroundColor: '#001D60',
                                            borderRadius: '14px',
                                            padding: '2px 40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: 'none',
                                        }}
                                        onClick={() => window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')}
                                    >
                                        Google Maps
                                        <img src={up_right_arrow} alt="" style={{ width: 14, height: 14, marginLeft: 3, filter: 'brightness(0) invert(1)' }} />
                                    </Button>
                                )}
                                </div>
                            </Col>
                        </Row>

                        {/* Metadata row */}
                        <div className="d-flex flex-wrap gap-4 border-top pt-3 mb-3">
                            {task.dueDate && (
                                <div className="d-flex align-items-center gap-1" style={task.isOverdue ? { color: '#dc3545' } : { color: '#6c757d' }}>
                                    <Clock size={16} />
                                    <span>Förfallodatum: {formatDateForDisplay(task.dueDate)}</span>
                                </div>
                            )}
                            {inspection?.template && (
                                <div className="d-flex align-items-center gap-1 text-muted">
                                    <RefreshCw size={16} />
                                    <span>{formatInterval(effectiveInspectionValues?.interval || inspection.template.interval)}</span>
                                </div>
                            )}
                            {inspection?.lastCompletedAt && (
                                <div className="d-flex align-items-center gap-1 text-muted">
                                    <CheckCircle size={16} />
                                    <span>Senast genomförd: {formatDateForDisplay(inspection.lastCompletedAt)}</span>
                                </div>
                            )}
                            {getAssignmentNames(task, getUserName) && (
                                <div className="d-flex align-items-center gap-1 text-muted">
                                    <User size={16} />
                                    <span>{getAssignmentNames(task, getUserName)}</span>
                                </div>
                            )}
                        </div>

                        {/* Collapsible details */}
                        {inspection?.template && (
                            <div className="border-top pt-3">
                                <Button
                                    variant="link"
                                    onClick={toggleDetails}
                                    className="p-0 text-decoration-none d-flex align-items-center gap-1"
                                >
                                    {showDetails ? 'Dölj detaljer' : 'Visa detaljer'}
                                    {showDetails ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </Button>

                                {showDetails && (
                                    <div className="mt-3 bg-light rounded p-3 small">
                                        {inspection.template.estimatedTime && (
                                            <div className="mb-1">
                                                <span className="text-muted">Uppskattad tid:</span>{' '}
                                                <span>{inspection.template.estimatedTime} min</span>
                                            </div>
                                        )}
                                        {(effectiveInspectionValues?.cost || inspection.template.estimatedCost) > 0 && (
                                            <div className="mb-1">
                                                <span className="text-muted">Uppskattad kostnad:</span>{' '}
                                                <span>{effectiveInspectionValues?.cost || inspection.template.estimatedCost} kr</span>
                                            </div>
                                        )}
                                        {inspection.lastMeasurement && (
                                            <div className="mb-1">
                                                <span className="text-muted">Senaste mätning:</span>{' '}
                                                <span>
                                                    {inspection.lastMeasurement.value} {inspection.lastMeasurement.unit}
                                                    {!inspection.lastMeasurement.withinRange && (
                                                        <Badge bg="warning" className="ms-2">Utanför gräns</Badge>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        <div className="border-top pt-2 mt-2">
                                            <div className="mb-1">
                                                <span className="text-muted">Startdatum:</span>{' '}
                                                <span>{formatDateForDisplay(inspection.startDate)}</span>
                                            </div>
                                            <div>
                                                <span className="text-muted">Tillagd:</span>{' '}
                                                <span>{formatDateTimeForDisplay(inspection.addedTime)} av {getUserName(inspection.addedBy)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    /* ── Work order layout (to be redesigned) ── */
                    <>
                        <div className="mb-3 d-flex align-items-center justify-content-between gap-2">
                            <h5 className="mb-0">{task.title}</h5>
                            {task.priority && (
                                <Badge bg={workOrderPriorityColors[task.priority]} className="priority-badge flex-shrink-0">
                                    {translateWorkOrderPriority(task.priority)}
                                </Badge>
                            )}
                        </div>

                        <div className="mb-3 d-flex align-items-center gap-2 text-muted">
                            <Building size={16} />
                            <a href={buildingLink} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                {task.buildingName}
                                <ExternalLink size={12} className="ms-1" />
                            </a>
                        </div>

                        {componentLink && (
                            <div className="mb-3 d-flex align-items-center gap-2 text-muted">
                                <LinkIcon size={16} />
                                <a href={componentLink} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                    {componentName || 'Komponent'}
                                    <ExternalLink size={12} className="ms-1" />
                                </a>
                            </div>
                        )}

                        {workOrder && buildingSummary && (
                            <div className="mb-3 bg-light rounded p-3">
                                <LocationSection
                                    buildingKey={task.buildingKey}
                                    buildingSummary={buildingSummary}
                                    component={component}
                                    showVisualMarkers={true}
                                    compact={true}
                                />
                            </div>
                        )}

                        {workOrder?.description && (
                            <div className="mb-3">
                                <div className="d-flex align-items-center gap-2 text-muted mb-1">
                                    <FileText size={16} />
                                    <small>Beskrivning</small>
                                </div>
                                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{workOrder.description}</p>
                            </div>
                        )}

                        {task.dueDate && (
                            <div className="mb-3 d-flex align-items-center gap-2">
                                <Clock size={16} className={task.isOverdue ? 'text-danger' : 'text-muted'} />
                                <span className={task.isOverdue ? 'text-danger' : ''}>
                                    Förfallodatum: {formatDateForDisplay(task.dueDate)}
                                </span>
                            </div>
                        )}

                        {workOrder && (workOrder.requiresPhoto || workOrder.requiresComment || workOrder.requiresConditionUpdate) && (
                            <div className="mb-3">
                                <small className="text-muted d-block mb-2">Krav för slutförande:</small>
                                <div className="d-flex flex-wrap gap-2">
                                    {workOrder.requiresPhoto && (
                                        <Badge bg="light" text="dark" className="d-flex align-items-center gap-1">
                                            <Camera size={12} /> Foto
                                        </Badge>
                                    )}
                                    {workOrder.requiresComment && (
                                        <Badge bg="light" text="dark" className="d-flex align-items-center gap-1">
                                            <MessageSquare size={12} /> Kommentar
                                        </Badge>
                                    )}
                                    {workOrder.requiresConditionUpdate && (
                                        <Badge bg="light" text="dark" className="d-flex align-items-center gap-1">
                                            <CheckSquare size={12} /> Skickuppdatering
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        )}

                        {getAssignmentNames(task, getUserName) && (
                            <div className="mb-3 d-flex align-items-center gap-2">
                                <User size={16} className="text-muted" />
                                <span>Tilldelad: {getAssignmentNames(task, getUserName)}</span>
                            </div>
                        )}

                        <div className="border-top pt-3 mt-3">
                            <Button
                                variant="link"
                                onClick={toggleDetails}
                                className="p-0 text-decoration-none d-flex align-items-center gap-1"
                            >
                                {showDetails ? 'Dölj detaljer' : 'Visa detaljer'}
                                {showDetails ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </Button>

                            {showDetails && workOrder && (
                                <div className="mt-3">
                                    {componentLink && (
                                        <div className="mb-3 d-flex align-items-center gap-2">
                                            <LinkIcon size={16} className="text-muted" />
                                            <span className="text-muted">Kopplad komponent:</span>
                                            <a href={componentLink} target="_blank" rel="noopener noreferrer">
                                                Visa komponent <ExternalLink size={12} />
                                            </a>
                                        </div>
                                    )}
                                    {workOrder.sourceIssueKey && (
                                        <div className="mb-3 d-flex align-items-center gap-2">
                                            <Tag size={16} className="text-muted" />
                                            <span className="text-muted">Skapad från felanmälan</span>
                                        </div>
                                    )}
                                    {workOrder.maintenanceTemplateId && (
                                        <div className="mb-3 d-flex align-items-center gap-2">
                                            <Calendar size={16} className="text-muted" />
                                            <span className="text-muted">Skapad från underhållsaktivitet</span>
                                        </div>
                                    )}
                                    <div className="bg-light rounded p-3 small">
                                        <div className="row">
                                            <div className="col-6 mb-2">
                                                <span className="text-muted">Skapad:</span>
                                                <div>{formatDateTimeForDisplay(workOrder.createdAt)}</div>
                                                <div className="text-muted">{getUserName(workOrder.createdBy)}</div>
                                            </div>
                                            {workOrder.updatedAt && (
                                                <div className="col-6 mb-2">
                                                    <span className="text-muted">Uppdaterad:</span>
                                                    <div>{formatDateTimeForDisplay(workOrder.updatedAt)}</div>
                                                    {workOrder.updatedBy && (
                                                        <div className="text-muted">{getUserName(workOrder.updatedBy)}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {isAdmin && onSnooze && !task.inspection?.snoozedUntil && (
                    <Button variant="outline-secondary" onClick={() => setShowSnoozeModal(true)}>
                        <ClockIcon size={18} className="me-1" />
                        Snooza
                    </Button>
                )}
                {isAdmin && onUnsnooze && task.inspection?.snoozedUntil && (
                    <Button variant="outline-warning" onClick={handleUnsnooze} disabled={unsnoozing}>
                        <ClockIcon size={18} className="me-1" />
                        {unsnoozing ? 'Avsnoozar...' : 'Avsnooza'}
                    </Button>
                )}
                <Button variant="primary" onClick={handleAssign}>
                    Tilldela
                </Button>
            </Modal.Footer>

            <SnoozeModal
                show={showSnoozeModal}
                onHide={() => setShowSnoozeModal(false)}
                itemName={task.title}
                onSnooze={handleSnooze}
            />
        </Modal>
    );
};

export default TaskDetailModal;
